import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  AppBar,
  Toolbar,
  ListItem,
  List,
  Avatar,
  IconButton,
} from "@mui/material";
import Atras from "./atras";
import { auth, db } from "../firebase";
import { collection, query, where, orderBy, onSnapshot, updateDoc } from "firebase/firestore";
import BottomToolbar from "./BottomNav";
import LogoLoader from "./LogoLoader";
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import PublishIcon from '@mui/icons-material/Publish';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      console.error("No user is currently authenticated");
      setLoading(false);
      return;
    }

    const notificationsRef = collection(db, "notifications");
    const userNotificationsQuery = query(
      notificationsRef,
      where("userId", "==", user.uid),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(userNotificationsQuery, (snapshot) => {
      const notificationsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setNotifications(notificationsData);
      setLoading(false);

      // Marcar todas las notificaciones como leídas
      snapshot.docs.forEach((doc) => {
        if (!doc.data().read) {
          updateDoc(doc.ref, { read: true });
        }
      });
    });

    return () => unsubscribe();
  }, []);

  const formatDate = (timestamp) => {
    if (timestamp && timestamp.toDate) {
      const date = timestamp.toDate();
      return date.toLocaleDateString('es-ES', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return "";
  };

  const handleNotificationClick = (notification) => {
    switch(notification.type) {
      case "reservation":
        navigate("/reservas");
        break;
      case "message":
        navigate("/chats");
        break;
      case "payment":
        navigate("/pagos");
        break;
      case "publication":
        navigate("/publicaciones");
        break;
      default:
        console.log("Acción no definida para este tipo de notificación");
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case "reservation":
        return <FlightTakeoffIcon sx={styles.icon} />;
      case "message":
        return <EmailIcon sx={styles.icon} />;
      case "payment":
        return <PaymentIcon sx={styles.icon} />;
      case "publication":
        return <PublishIcon sx={styles.icon} />;
      default:
        return <FlightTakeoffIcon sx={styles.icon} />;
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case "reservation":
        return "#4CAF50";
      case "message":
        return "#2196F3";
      case "payment":
        return "#FF9800";
      case "publication":
        return "#9C27B0";
      default:
        return "#9E9E9E";
    }
  };

  const getNotificationTitle = (type) => {
    switch(type) {
      case "reservation":
        return "Reserva confirmada";
      case "message":
        return "Nuevo mensaje";
      case "payment":
        return "Pago exitoso";
      case "publication":
        return "Publicación exitosa";
      default:
        return "Notificación";
    }
  };

  const renderNotificationItem = (notification) => {
    const notificationColor = getNotificationColor(notification.type);
    return (
      <ListItem 
        key={notification.id} 
        sx={{
          ...styles.listItem,
          borderLeft: `4px solid ${notificationColor}`,
        }}
        onClick={() => handleNotificationClick(notification)}
      >
        <Box sx={styles.avatarContainer}>
          <Avatar sx={{ bgcolor: notificationColor }}>
            {getNotificationIcon(notification.type)}
          </Avatar>
        </Box>
        <Box sx={styles.contentContainer}>
          <Typography variant="subtitle1" sx={styles.notificationType}>
            {getNotificationTitle(notification.type)}
          </Typography>
          <Typography variant="body2" sx={styles.notificationMessage}>
            {notification.message}
          </Typography>
          <Typography variant="caption" sx={styles.notificationTimestamp}>
            {formatDate(notification.timestamp)}
          </Typography>
        </Box>
      </ListItem>
    );
  };

  const styles = {
    appBar: {
      bgcolor: "black",
    },
    title: {
      flexGrow: 1,
      textAlign: "center",
      fontWeight: "bold",
    },
    container: {
      mt: 10,
      mb: 10,
      px: 2,
    },
    listItem: {
      display: "flex",
      alignItems: "flex-start",
      padding: 2,
      mb: 2,
      borderRadius: 2,
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
      },
    },
    avatarContainer: {
      mr: 2,
    },
    contentContainer: {
      flexGrow: 1,
    },
    notificationType: {
      fontWeight: "bold",
      color: "#4ade80",
    },
    notificationMessage: {
      color: "text.secondary",
      mb: 1,
    },
    notificationTimestamp: {
      color: "text.disabled",
    },
    icon: {
      color: "white",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "50vh",
    },
    noNotificationsText: {
      textAlign: "center",
      mt: 4,
      color: "text.secondary",
    },
  };

  return (
    <Box>
      <AppBar position="fixed" sx={styles.appBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <Atras />
          </IconButton>
          <Typography variant="h6" sx={styles.title}>
            Notificaciones
          </Typography>
        </Toolbar>
      </AppBar>
      <Container sx={styles.container}>
        {loading ? (
          <LogoLoader label="Cargando notificaciones..." minHeight={250} />
        ) : notifications.length > 0 ? (
          <List>
            {notifications.map(renderNotificationItem)}
          </List>
        ) : (
          <Typography variant="body1" sx={styles.noNotificationsText}>
            No tienes notificaciones.
          </Typography>
        )}
      </Container>
      <BottomToolbar />
    </Box>
  );
};

export default Notifications;